import React from 'react'
import styled from 'styled-components'
import { skills } from '../../data/constants'
import { motion } from 'framer-motion'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding-bottom: 60px;
  scroll-margin-top: 100px;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`

export const Title = styled.div`
  font-family: 'Mosca Laroke', 'Playfair Display', 'Bodoni Moda', 'Cinzel', serif;
  font-size: 76px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  line-height: 1;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 32px;
  letter-spacing: 0.5px;

  @media (max-width: 1400px) { font-size: 64px; }
  @media (max-width: 1200px) { font-size: 52px; }
  @media (max-width: 768px)  { font-size: 44px; }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;
  gap: 30px;
  justify-content: center;
  align-items: stretch;
`

const Skill = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${({ theme }) => theme.card};
  border: 0.1px solid #306EE8;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  border-radius: 16px;
  padding: 18px 36px;
  transition: all 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: rgba(23, 92, 230, 0.3) 0px 8px 32px;
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    max-width: 400px;
    padding: 16px 24px;
  }
  @media (max-width: 500px) {
    max-width: 330px;
    padding: 14px 18px;
  }
`

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`

const CategoryIcon = styled.span`
  font-size: 24px;
`

const SkillTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
  margin: 0;
`

const SkillCount = styled.span`
  background: ${({ theme }) => theme.primary};
  color: white;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 8px;
`

const SkillList = styled.div`
  display: flex;
  justify-content: center; 
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`

const SkillItem = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.text_primary + 80};
  border: 1px solid ${({ theme }) => theme.text_primary + 80};
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease-in-out;
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 8px 16px rgba(133, 76, 230, 0.3);
    background: ${({ theme }) => theme.primary + 10};
    border-color: ${({ theme }) => theme.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 12px;
  }
  @media (max-width: 500px) {
    font-size: 14px;
    padding: 6px 12px;
  }
`

const SkillImage = styled.img`
  width: 24px;
  height: 24px;
`

const SkillInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const SkillName = styled.div`
  font-size: 16px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.text_primary + 20};
  border-radius: 2px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, #854CE6 0%, #9D5CE8 100%);
  width: ${props => props.level || 70}%;
  border-radius: 2px;
  transition: width 0.5s ease-in-out;
`

const categoryIcons = {
  'Frontend': '🎨',
  'Backend': '⚙️',
  'Android': '📱',
  'Machine Learning': '🤖',
  'Others': '🔧'
};

const Skills = () => {
  return (
    <Container id="skills">
      <Wrapper
        as={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Title>SKILLS</Title>
        <SkillsContainer>
          {skills.map((skill, index) => (
            <Skill key={index}>
              <SkillHeader>
                <CategoryIcon>{categoryIcons[skill.title] || '🔧'}</CategoryIcon>
                <SkillTitle>{skill.title}</SkillTitle>
                <SkillCount>{skill.skills.length}</SkillCount>
              </SkillHeader>
              <SkillList>
                {skill.skills.map((item, idx) => (
                  <SkillItem key={idx}>
                    <SkillImage src={item.image} alt={item.name} />
                    <SkillInfo>
                      <SkillName>{item.name}</SkillName>
                      <ProgressBarContainer>
                        <ProgressFill level={item.level || 70} />
                      </ProgressBarContainer>
                    </SkillInfo>
                  </SkillItem>
                ))}
              </SkillList>
            </Skill>
          ))}
        </SkillsContainer>
      </Wrapper>
    </Container>
  )
}

export default Skills